import { useEffect, useRef, useState } from "react";
import { Camera, Check, Loader2, RefreshCw, SwitchCamera } from "lucide-react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

// Live in-browser camera (works on desktop webcams and phone cameras). Captures a
// JPEG and hands it back as a File to attach to a customer folder.
export default function CameraCapture({ open, onClose, onCapture }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [facing, setFacing] = useState("environment");
  const [error, setError] = useState(null);
  const [shot, setShot] = useState(null);
  const [starting, setStarting] = useState(false);

  const stop = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  };

  const start = async () => {
    setError(null);
    setStarting(true);
    stop();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: facing }, audio: false });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => {});
      }
    } catch (e) {
      setError(
        e?.name === "NotAllowedError"
          ? "Camera access was blocked. Allow the camera in your browser's address bar, then tap Retry."
          : "No camera was found on this device. Use \"Upload photo or PDF\" instead."
      );
    } finally {
      setStarting(false);
    }
  };

  useEffect(() => {
    if (open && !shot) start();
    return () => { if (!open) stop(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, facing]);

  useEffect(() => () => stop(), []);

  const capture = () => {
    const v = videoRef.current;
    if (!v || !v.videoWidth) return;
    const c = document.createElement("canvas");
    c.width = v.videoWidth;
    c.height = v.videoHeight;
    c.getContext("2d").drawImage(v, 0, 0);
    c.toBlob((blob) => { if (blob) { setShot({ url: URL.createObjectURL(blob), blob }); stop(); } }, "image/jpeg", 0.9);
  };

  const retake = () => {
    if (shot) URL.revokeObjectURL(shot.url);
    setShot(null);
    start();
  };

  const cleanupClose = () => {
    if (shot) URL.revokeObjectURL(shot.url);
    setShot(null);
    stop();
    onClose();
  };

  const use = () => {
    if (!shot) return;
    const file = new File([shot.blob], `photo-${Date.now()}.jpg`, { type: "image/jpeg" });
    onCapture(file);
    cleanupClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && cleanupClose()}>
      <DialogContent className="max-w-lg bg-card" data-testid="camera-capture-modal">
        <DialogHeader><DialogTitle className="font-display text-2xl">Take a photo</DialogTitle></DialogHeader>
        <div className="relative rounded-lg overflow-hidden bg-black aspect-[3/4] flex items-center justify-center">
          {error ? (
            <p className="text-center text-sm text-destructive p-6" data-testid="camera-error">{error}</p>
          ) : shot ? (
            <img src={shot.url} alt="Captured" className="w-full h-full object-contain" data-testid="camera-preview-shot" />
          ) : (
            <video ref={videoRef} playsInline muted className="w-full h-full object-cover" data-testid="camera-video" />
          )}
          {starting && !shot && !error && <Loader2 className="animate-spin text-white absolute" />}
        </div>
        <DialogFooter className="flex-row justify-between gap-2 sm:justify-between">
          {error ? (
            <Button onClick={start} data-testid="camera-retry" className="hover:bg-blue-600 hover:text-white"><RefreshCw size={16} /> Retry</Button>
          ) : shot ? (
            <>
              <Button variant="outline" onClick={retake} data-testid="camera-retake"><RefreshCw size={16} /> Retake</Button>
              <Button onClick={use} data-testid="camera-use" className="hover:bg-blue-600 hover:text-white"><Check size={16} /> Use photo</Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => setFacing((f) => (f === "environment" ? "user" : "environment"))} data-testid="camera-flip"><SwitchCamera size={16} /> Flip</Button>
              <Button onClick={capture} disabled={starting} data-testid="camera-shutter" className="hover:bg-blue-600 hover:text-white"><Camera size={16} /> Capture</Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
